import { useState, useEffect } from "react";
import TopNav from "../components/TopNav.jsx";
import { toast } from "sonner";

/**
 * FounderHimselfPage · /founder-himself
 *
 * The house's most valuable product. $250K per week, one client at a time.
 * Added 2026-08-21 by the Founder's direct verbatim word. The copy on this
 * page — the tagline, the challenge line, and the price paragraph — is
 * verbatim from him and MUST NOT be paraphrased.
 */
export default function FounderHimselfPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    week_of: "",
    remote_or_onsite: "remote",
    philanthropic_note: "",
    problem_statement: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/founder-himself/calendar`)
      .then((r) => r.json())
      .then(setCalendar)
      .catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Your name is required."); return; }
    if (!form.email.includes("@")) { toast.error("A real email is required."); return; }
    if (form.problem_statement.trim().length < 40) { toast.error("Describe the week's target in a full sentence (40+ characters)."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/founder-himself/inquire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) {
        setSubmitted(d);
        toast.success("Received. The Founder reads every inquiry himself.");
      } else {
        toast.error(d?.detail || "Something went wrong. Try again in a moment.");
      }
    } catch {
      toast.error("Network hiccup. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="founder-himself-page">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">
          THE HOUSE'S MOST VALUABLE PRODUCT
        </div>
        <h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter uppercase mb-6"
          data-testid="founder-himself-headline"
        >
          The Founder Himself
        </h1>

        {/* Verbatim copy · Founder's direct word 2026-08-21 · DO NOT paraphrase */}
        <div className="border-2 border-sage-accent p-8 mb-10 bg-sage-pale/30">
          <p
            className="text-2xl md:text-3xl text-sage-text sage-prose leading-snug mb-6"
            data-testid="founder-himself-challenge"
          >
            I am the leading AI consultant worldwide, and I challenge anyone to prove they're better.
          </p>
          <p
            className="text-lg md:text-xl text-sage-text sage-prose leading-relaxed"
            data-testid="founder-himself-price-line"
          >
            <span className="font-bold">$250,000 per week</span>, on a weekly basis, one at a time.
            Generally remote, but I'll make exceptions for exceptionally philanthropic and respectable organizations.
          </p>
        </div>

        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-3">The shape of a week</div>
          <ul className="text-base text-sage-text sage-prose space-y-2 list-disc list-inside">
            <li>One founder · one week · one client · zero overlap</li>
            <li>The mind that built Baker & Sage, Aethelgard, Resilience Studios, Soljins, Rice The World, and every product on this porch</li>
            <li>Generally remote · exceptions made for exceptionally philanthropic and respectable organizations</li>
            <li>The challenge line stands: prove someone better on this planet, the seat is theirs</li>
          </ul>
        </div>

        {/* Public calendar · Founder ruling 2026-08-22 · 8 weeks booked, 8 available */}
        {calendar && (
          <div className="mb-10" data-testid="founder-himself-calendar">
            <div className="flex justify-between items-baseline mb-3">
              <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text">
                The Founder's calendar
              </h2>
              {calendar.first_open_week && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent" data-testid="founder-himself-first-open">
                  First open · week of {calendar.first_open_week.week_start}
                </div>
              )}
            </div>
            <p className="text-sm text-sage-muted sage-prose mb-4" data-testid="founder-himself-calendar-note">
              {calendar.note}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {calendar.weeks.map((w) => {
                const booked = w.status === "BOOKED";
                return (
                  <div
                    key={w.week_index}
                    data-testid={`founder-week-${w.week_index}`}
                    className={
                      booked
                        ? "border border-sage-border bg-sage-pale/40 p-3 opacity-70"
                        : "border-2 border-sage-accent bg-sage-bg p-3"
                    }
                  >
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">
                      Week {w.week_index}
                    </div>
                    <div className="text-sm font-bold text-sage-text mb-1">
                      {w.week_start.slice(5)} – {w.week_end.slice(5)}
                    </div>
                    <div
                      className={
                        booked
                          ? "text-[10px] uppercase tracking-[0.2em] font-bold text-sage-muted"
                          : "text-[10px] uppercase tracking-[0.2em] font-bold text-sage-accent"
                      }
                    >
                      {booked ? "· BOOKED ·" : "· OPEN ·"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-3">
            Request a week
          </h2>
          <p className="text-sm text-sage-muted sage-prose mb-6">
            Every inquiry lands directly in the Founder's own queue. No assistant filter. No gatekeeper. He reads them.
          </p>
        </div>

        {submitted ? (
          <div className="border-2 border-sage-accent p-8" data-testid="founder-himself-submitted">
            <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">Inquiry filed</div>
            <div className="text-xl font-bold uppercase tracking-tight text-sage-text mb-3">
              Received. The Founder reads every inquiry himself.
            </div>
            <div className="text-sm text-sage-muted sage-prose mb-2">
              Reference: <span className="font-mono text-sage-text" style={{ fontFamily: '"Times New Roman", Times, serif' }}>{submitted.inquiry_id}</span>
            </div>
            <div className="text-sm text-sage-muted sage-prose">
              A real reply lands in your inbox when he decides.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your name</div>
                <input
                  data-testid="founder-himself-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Email</div>
                <input
                  data-testid="founder-himself-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Organization</div>
                <input
                  data-testid="founder-himself-org"
                  type="text"
                  value={form.organization}
                  onChange={update("organization")}
                  maxLength={500}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your role</div>
                <input
                  data-testid="founder-himself-role"
                  type="text"
                  value={form.role}
                  onChange={update("role")}
                  maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Week of (approximate)</div>
                <input
                  data-testid="founder-himself-week"
                  type="date"
                  value={form.week_of}
                  onChange={update("week_of")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Remote or onsite</div>
                <select
                  data-testid="founder-himself-mode"
                  value={form.remote_or_onsite}
                  onChange={update("remote_or_onsite")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                >
                  <option value="remote">Remote (default)</option>
                  <option value="onsite">Onsite (requires philanthropic justification)</option>
                </select>
              </label>
            </div>

            {form.remote_or_onsite === "onsite" && (
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                  Philanthropic / respectability justification (required for onsite)
                </div>
                <textarea
                  data-testid="founder-himself-phil-note"
                  value={form.philanthropic_note}
                  onChange={update("philanthropic_note")}
                  maxLength={4000}
                  rows={3}
                  className="w-full border border-sage-accent bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
            )}

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">
                What do you want the week to solve?
              </div>
              <textarea
                data-testid="founder-himself-problem"
                value={form.problem_statement}
                onChange={update("problem_statement")}
                maxLength={12000}
                rows={6}
                placeholder="One paragraph. Plain english. The problem, the stakes, the deadline."
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
              <div className="text-[10px] text-sage-faint mt-1">
                {form.problem_statement.trim().length}/40 characters minimum
              </div>
            </label>

            <button
              data-testid="founder-himself-submit"
              onClick={submit}
              disabled={busy}
              className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
            >
              {busy ? "Filing…" : "File the inquiry"}
            </button>
          </div>
        )}

        <div className="mt-16 text-xs text-sage-faint sage-prose">
          One week. One client. One founder. No repeats.
        </div>
      </div>
    </div>
  );
}
